import { useState, useEffect } from 'react';
import { dashboardHandler, CustomDateParams, createCancelSource, isRequestCancelled } from '../DashboardHandler';
import { mapTopPerformersResponse } from '../utils/mappers';
import type { Performer, CustomDateRange } from '../types';
import { getComparisonDays } from '../Components/DateRangePicker';

interface UseTopPerformersReturn {
  performers: Performer[];
  loading: boolean;
  error: string | null;
}

const REFRESH_INTERVAL_MS = 60000;

export const useTopPerformers = (period: string, customRange?: CustomDateRange): UseTopPerformersReturn => {
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Don't fetch if custom mode but no range provided
    if (period === 'custom' && !customRange) return;

    const source = createCancelSource();
    let isMounted = true;
    let isFetching = false;

    const fetchData = async (showLoading: boolean) => {
      if (isFetching) return;

      isFetching = true;
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        let customDate: CustomDateParams | undefined;
        if (period === 'custom' && customRange) {
          customDate = {
            startDate: customRange.startDate,
            endDate: customRange.endDate,
            comparisonDays: getComparisonDays(customRange),
          };
        }

        const result = await dashboardHandler.getTopPerformers(period, source.token, customDate);

        if (isMounted) {
          const mappedPerformers = mapTopPerformersResponse(result);

          setPerformers(mappedPerformers);
        }
      } catch (err: any) {
        if (isMounted && !isRequestCancelled(err)) {
          console.error('Failed to fetch top performers:', err);
          setError(err.message || 'Failed to load top performers');
        }
      } finally {
        isFetching = false;
        if (isMounted && showLoading) {
          setLoading(false);
        }
      }
    };

    fetchData(true);

    const refreshData = () => fetchData(false);
    const intervalId = window.setInterval(refreshData, REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refreshData);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshData);
      source.cancel('Period changed or component unmounted');
    };
  }, [period, customRange]);

  return { performers, loading, error };
};

// Retained for compatibility with the hooks barrel.
export const clearTopPerformersCache = () => undefined;
